import { ReducerCreators } from '@reduxjs/toolkit';
import { AudiencesState } from '../types';
import LoadAttendees from '@/connection/audiences/all-audiences';
import type { RootState } from '@/store/root-state';
import {
  selectListAttendeesFields,
  selectListAttendeesPage,
  selectListAttendeesPageSize,
  selectListAttendeesSearch,
  selectListAttendeesSort,
  selectListAttendeesSortDirection,
} from '../selectors';
import { selectWorkingProjectUuid } from '@/store/slices/project/selectors';
import { selectActiveChannel } from '@/store/slices/user/selectors';
import type { AttendeeFields, RegistrationFilter } from '@/types/all-attendees.types';
import deepEqual from 'fast-deep-equal';

// registrationStatus comes in as an argument rather than from the store: its source of truth
// is the registrationStatus URL search param.
export type FetchListAttendeesArgs = {
  initFields?: AttendeeFields;
  passcodeListId: number;
  registrationStatus?: RegistrationFilter;
};

export const fetchListAttendees = (create: ReducerCreators<AudiencesState>) =>
  create.asyncThunk(
    async ({ initFields, passcodeListId, registrationStatus }: FetchListAttendeesArgs, { getState, signal }) => {
      const state = getState() as RootState;
      const channel = selectActiveChannel(state);
      const eventUuid = selectWorkingProjectUuid(state)!;
      const page = selectListAttendeesPage(state);
      const pageSize = selectListAttendeesPageSize(state);
      const search = selectListAttendeesSearch(state);
      const sort = selectListAttendeesSort(state);
      const sortDirection = selectListAttendeesSortDirection(state);
      const fields = initFields ?? selectListAttendeesFields(state);

      return await LoadAttendees({
        channel,
        eventUuid,
        page: page - 1,
        pageSize,
        search,
        sort,
        sortDirection,
        fields,
        registrationStatusFilter: registrationStatus,
        passcodeListId,
        signal,
      });
    },
    {
      options: {
        condition: ({ initFields }, { getState }) => {
          const state = getState() as RootState;
          const fields = selectListAttendeesFields(state);

          if (deepEqual(fields, {})) {
            return false;
          }

          if (initFields && fields && deepEqual(initFields, fields)) {
            return !state.audiences.listAttendees.loading;
          }

          return true;
        },
      },
      pending: (state) => {
        state.listAttendees.loading = true;
      },
      fulfilled: (state, action) => {
        state.listAttendees.users = action.payload.data || [];
        state.listAttendees.total = action.payload.total || 0;
        state.listAttendees.loading = false;
      },
      rejected: (state, action) => {
        if (action.meta.aborted) return;
        state.listAttendees.loading = false;
        console.error(action.error);
      },
    }
  );
